
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeProvider as BrandThemeProvider } from "@/theme/ThemeProvider";
import { useGlobalRevealHotkey } from "@/theme/copy/useGlobalRevealHotkey";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import DeviceDetail from "@/pages/DeviceDetail";
import ScriptsPage from "@/pages/ScriptsPage";
import CompliancePage from "@/pages/CompliancePage";
import ComplianceBenchmarksPage from "@/pages/ComplianceBenchmarksPage";
import SoftwareUpdatesPage from "@/pages/SoftwareUpdatesPage";
import NewSoftwareUpdatePolicyPage from "@/pages/NewSoftwareUpdatePolicyPage";
import EditSoftwareUpdatePolicyPage from "@/pages/EditSoftwareUpdatePolicyPage";
import SoftwareUpdatePolicyDetailPage from "@/pages/SoftwareUpdatePolicyDetailPage";
import VulnerabilitiesPage from "@/pages/VulnerabilitiesPage";
import NewPatchPolicyPage from "@/pages/NewPatchPolicyPage";
import GettingStarted from "@/pages/GettingStarted";
import { useIsMobile } from "@/hooks/use-mobile";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import { Toaster } from "@/components/ui/toaster";
import ConfigurationDetail from "./pages/ConfigurationDetail";
import Configurations from "./pages/Configurations";
import NewConfiguration from "./pages/NewConfiguration";
import NotFound from "./pages/NotFound";
import ConfigurationsPage from "./pages/ConfigurationsPage";
import PppcPoliciesPage from "./pages/PppcPoliciesPage";
import NewPppcPolicyPage from "./pages/NewPppcPolicyPage";
import EditPppcPolicyPage from "./pages/EditPppcPolicyPage";
import PppcPolicyDetailPage from "./pages/PppcPolicyDetailPage";
import AppConfigurationsPage from "./pages/AppConfigurationsPage";
import NewAppConfigPage from "./pages/NewAppConfigPage";
import EditAppConfigPage from "./pages/EditAppConfigPage";
import NewProfilePage from "./pages/NewProfilePage";
import EditProfilePage from "./pages/EditProfilePage";
import NewEnrollmentProfilePage from "./pages/NewEnrollmentProfilePage";
import EditEnrollmentProfilePage from "./pages/EditEnrollmentProfilePage";
import EnrollmentProfilesPage from "./pages/EnrollmentProfilesPage";
import AppConfigDialogHarness from "./pages/__e2e/AppConfigDialogHarness";
import WorkspacesPage from "./pages/admin/WorkspacesPage";
import WorkspaceNewPage from "./pages/admin/WorkspaceNewPage";
import WorkspaceEditPage from "./pages/admin/WorkspaceEditPage";
import { WorkspaceProvider } from "@/workspace/WorkspaceProvider";
import PppcInstallPollerMount from "@/components/system/PppcInstallPollerMount";

import PrivateRoute from "./components/PrivateRoute";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import PreferencesPage from "./pages/PreferencesPage";
import EnrolDevicePage from "./pages/EnrolDevicePage";
import Index from "./pages/Index";
import AdminDashboard from "./pages/AdminDashboard";
import RBACPage from "./pages/RBACPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import AddApplicationPage from "./pages/AddApplicationPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import ProfileDetailPage from "./pages/ProfileDetailPage";
import TagsPage from "./pages/TagsPage";
import TagDetailPage from "./pages/TagDetailPage";
import EnrollmentProfileDetailPage from "./pages/EnrollmentProfileDetailPage";
import PortalPrivateRoute from "./components/PortalPrivateRoute";
import MyDevicesPage from "./pages/portal/MyDevicesPage";
import DeviceEnrollmentPage from "./pages/portal/DeviceEnrollmentPage";
import PendingApprovalPage from "./pages/portal/PendingApprovalPage";
import TermsAndConditionsPage from "./pages/portal/TermsAndConditionsPage";
import UsersAndRolesPage from "./pages/UsersAndRolesPage";
import UnifiedUserDetailPage from "./pages/UnifiedUserDetailPage";
import LogsPage from "./pages/LogsPage";
import SettingsPage from "./pages/SettingsPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import ComingSoonPreviewPage from "./pages/ComingSoonPreviewPage";
import TenantsPage from "./pages/admin/TenantsPage";
import OnboardTenantPage from "./pages/admin/OnboardTenantPage";
import TenantDetailsPage from "./pages/admin/TenantDetailsPage";
import DevicesPage from "./pages/DevicesPage";
import MobileDashboardPage from "./pages/mobile/MobileDashboardPage";
import MobileDevicesPage from "./pages/mobile/MobileDevicesPage";
import MobileDeviceDetailPage from "./pages/mobile/MobileDeviceDetailPage";
import MobileApprovalsPage from "./pages/mobile/MobileApprovalsPage";
import MobileApprovalDetailPage from "./pages/mobile/MobileApprovalDetailPage";
import MobileSecurityPage from "./pages/mobile/MobileSecurityPage";

import "./App.css";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import ErrorBoundary from "./components/ErrorBoundary";

const ProfileRedirect = ({ edit }: { edit?: boolean }) => {
  const { id } = useParams();
  return <Navigate to={`/configurations/${id}${edit ? '/edit' : ''}`} replace />;
};

const EnrollmentRedirect = ({ edit }: { edit?: boolean }) => {
  const { id } = useParams();
  return <Navigate to={`/admin/enrollment-profiles/${id}${edit ? '/edit' : ''}`} replace />;
};

/** Mounts the `?` keyboard shortcut that toggles "reveal all themed copy". Must live inside <BrandThemeProvider>. */
const CopyHotkeys = () => {
  useGlobalRevealHotkey();
  return null;
};

function HomeRedirect() {
  const isMobile = useIsMobile();
  return isMobile ? <Navigate to="/m/dashboard" replace /> : <GettingStarted />;
}

function App() {


  return (
    <ThemeProvider 
      attribute="class" 
      defaultTheme="light" 
      enableSystem={false}
      disableTransitionOnChange={false}
      storageKey="vite-ui-theme"
    >
      <BrandThemeProvider>
      <CopyHotkeys />
      <PppcInstallPollerMount />
      <NotificationProvider>
        <ErrorBoundary>
          <Router>
            <AnalyticsProvider>
              <Routes>
              {/* Main Admin Portal Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/welcome" element={<Index />} />
              <Route path="/coming-soon-preview" element={<ComingSoonPreviewPage />} />

              {/* Dev-only E2E harness routes (excluded from production builds). */}
              {import.meta.env.DEV && (
                <Route
                  path="/__e2e/app-config-dialog"
                  element={<AppConfigDialogHarness />}
                />
              )}

              {/* Device Portal Routes */}
              <Route path="/enrol" element={<Navigate to="/enrol/device" replace />} />
              <Route path="/enrol/device" element={<EnrolDevicePage />} />
              <Route path="/portal" element={<Navigate to="/enrol/device" replace />} />
              <Route path="/portal/login" element={<Navigate to="/enrol/device" replace />} />
              <Route path="/portal/pending-approval" element={<PendingApprovalPage />} />
              <Route path="/portal/terms" element={<TermsAndConditionsPage />} />
              <Route 
                path="/portal/mydevices" 
                element={
                  <PortalPrivateRoute>
                    <MyDevicesPage />
                  </PortalPrivateRoute>
                } 
              />
              <Route 
                path="/portal/enroll" 
                element={
                  <PortalPrivateRoute>
                    <DeviceEnrollmentPage />
                  </PortalPrivateRoute>
                } 
              />
              
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <WorkspaceProvider>
                      <Layout />
                    </WorkspaceProvider>
                  </PrivateRoute>
                }
              >
                <Route index element={<HomeRedirect />} />
                <Route path="getting-started" element={<GettingStarted />} />

                {/* Mobile-only redesigned shell (4-tab IA). On md+ these
                    redirect to the canonical desktop routes. */}
                <Route path="m/dashboard" element={<MobileDashboardPage />} />
                <Route path="m/devices" element={<MobileDevicesPage />} />
                <Route path="m/devices/:id" element={<MobileDeviceDetailPage />} />
                <Route path="m/approvals" element={<MobileApprovalsPage />} />
                <Route path="m/approvals/:id" element={<MobileApprovalDetailPage />} />
                <Route path="m/security" element={<MobileSecurityPage />} />

                <Route path="devices" element={<DevicesPage />} />
                <Route path="devices/:id" element={<DeviceDetail />} />
                <Route path="analytics" element={<Dashboard />} />
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/users-and-roles" element={<UsersAndRolesPage />} />
                <Route path="admin/users-and-roles/:userId" element={<UnifiedUserDetailPage />} />
                <Route path="admin/enrollment-profiles" element={<EnrollmentProfilesPage />} />
                <Route path="admin/enrollment-profiles/new" element={<NewEnrollmentProfilePage />} />
                <Route path="admin/enrollment-profiles/:id" element={<EnrollmentProfileDetailPage />} />
                <Route path="admin/enrollment-profiles/:id/edit" element={<EditEnrollmentProfilePage />} />
                <Route path="admin/tenants" element={<TenantsPage />} />
                <Route path="admin/tenants/onboard" element={<OnboardTenantPage />} />
                <Route path="admin/tenants/:id" element={<TenantDetailsPage />} />
                <Route path="admin/workspaces" element={<WorkspacesPage />} />
                <Route path="admin/workspaces/new" element={<WorkspaceNewPage />} />
                <Route path="admin/workspaces/:id/edit" element={<WorkspaceEditPage />} />
                {/* Redirects for backward compatibility */}
                <Route path="admin/rbac" element={<Navigate to="/admin/users-and-roles?tab=roles" replace />} />
                <Route path="admin/directory-users" element={<Navigate to="/admin/users-and-roles?filter=directory" replace />} />
                <Route path="admin/directory-users/:userId" element={<Navigate to="/admin/users-and-roles/:userId" replace />} />
                <Route path="scripts" element={<ScriptsPage />} />
                <Route path="compliance" element={<CompliancePage />} />
                <Route
                  path="compliance/benchmarks"
                  element={<ComplianceBenchmarksPage />}
                />
                <Route path="software-updates" element={<SoftwareUpdatesPage />} />
                <Route path="software-updates/policies/new" element={<NewSoftwareUpdatePolicyPage />} />
                <Route path="software-updates/policies/:id" element={<SoftwareUpdatePolicyDetailPage />} />
                <Route path="software-updates/policies/:id/edit" element={<EditSoftwareUpdatePolicyPage />} />
                <Route path="patching" element={<Navigate to="/software-updates" replace />} />
                <Route path="patching/new" element={<NewPatchPolicyPage />} />
                <Route path="vulnerabilities" element={<VulnerabilitiesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                {/*
                  API Keys and Theme Generator are temporarily hidden from
                  navigation. Routes resolve to a "coming soon" empty state so
                  bookmarked / legacy URLs degrade gracefully.
                */}
                <Route path="settings/api-keys" element={<ComingSoonPage title="API Keys" description="API key management isn't available yet. We'll enable it once the underlying integrations ship." />} />
                <Route path="settings/byok" element={<Navigate to="/settings/api-keys" replace />} />
                <Route path="settings/theme-generator" element={<ComingSoonPage title="Theme Generator" description="The AI theme generator is being polished. It will return in a future release." />} />
                <Route path="configs" element={<Navigate to="/configurations" replace />} />
                <Route path="configs/new" element={<Navigate to="/configurations/new" replace />} />
                <Route path="configs/:id" element={<ConfigurationDetail />} />
                {/* Legacy portal used /configuration (singular) — preserve bookmarks. */}
                <Route path="configuration" element={<Navigate to="/configurations" replace />} />
                <Route path="configuration/new" element={<Navigate to="/configurations/new" replace />} />
                <Route path="configurations" element={<ConfigurationsPage />} />
                <Route path="configurations/new" element={<NewConfiguration />} />
                <Route path="configurations/pppc" element={<PppcPoliciesPage />} />
                <Route path="configurations/pppc/new" element={<NewPppcPolicyPage />} />
                <Route path="configurations/pppc/:id" element={<PppcPolicyDetailPage />} />
                <Route path="configurations/pppc/:id/edit" element={<EditPppcPolicyPage />} />
                <Route path="configurations/app-config" element={<AppConfigurationsPage />} />
                <Route path="configurations/app-config/new" element={<NewAppConfigPage />} />
                <Route path="configurations/app-config/:id/edit" element={<EditAppConfigPage />} />
                <Route path="configurations/:id" element={<ProfileDetailPage />} />
                <Route path="configurations/:id/edit" element={<EditProfilePage />} />
                {/* Backward compatibility redirects for old /profiles routes */}
                <Route path="profiles" element={<Navigate to="/configurations" replace />} />
                <Route path="profiles/new" element={<Navigate to="/configurations/new" replace />} />
                <Route path="profiles/:id" element={<ProfileRedirect />} />
                <Route path="profiles/:id/edit" element={<ProfileRedirect edit />} />
                <Route path="profiles/enrollment/new" element={<Navigate to="/admin/enrollment-profiles/new" replace />} />
                <Route path="profiles/enrollment/:id" element={<EnrollmentRedirect />} />
                <Route path="profiles/enrollment/:id/edit" element={<EnrollmentRedirect edit />} />
                
                <Route path="apps" element={<Navigate to="/applications" replace />} />
                <Route path="apps/:id" element={<Navigate to="/applications/:id" replace />} />
                <Route path="dashboard" element={<Navigate to="/analytics" replace />} />
                <Route path="applications" element={<ApplicationsPage />} />
                <Route path="applications/add" element={<AddApplicationPage />} />
                <Route path="applications/:id" element={<ApplicationDetailPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="tags/:tagId" element={<TagDetailPage />} />
                <Route path="logs" element={<LogsPage />} />
                <Route path="preferences" element={<PreferencesPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              </Routes>
              <Toaster />
            </AnalyticsProvider>
          </Router>
        </ErrorBoundary>
      </NotificationProvider>
      </BrandThemeProvider>
    </ThemeProvider>
  );
}

export default App;
